import { FC, ReactNode, useEffect, useRef, useState } from "react";
import Modal from "../Modal/Modal";
import styles from './ConversationModal.module.scss';
import appState from "../../../state/AppStateContainer";
import Editor from "../Editor/Editor";
import { htmlToPlainTest } from "../../../util";
import { useAnimatedHeight } from "../../../util/hooks";
import { useTranslation } from "react-i18next";
import BtButton from "../../../ts-components/bt-button/bt-button";
import Badge from "../Badge/Badge";
import ConversationMessage from "./ConversationMessage";
import { reportConversationError } from "./reportConversationError";

type Message = {
  _id: string,
  content: string,
  addedBy: {
    _id: string,
    name: string,
    avatarUrl: string,
  },
  addedAt: string,
  updatedAt: string,
}

type Props = {
  title: string,
  fetchConversation: () => Promise<Message[]>,
  updateConversation: (content: string) => Promise<Message[]>,
  editMessage?: (messageId: string, content: string) => Promise<Message[]>,
  deleteMessage?: (messageId: string) => Promise<Message[]>,
  render: (hasContent:boolean, content: ReactNode, openModal: () => void, count: number, author: string, date: Date) => JSX.Element,
}

const ConversationModal: FC<Props> = ({ title, fetchConversation, updateConversation, editMessage, deleteMessage, render }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'conversationModal' });
  const [isModalShown, setIsModalShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [conversation, setConversation] = useState<Message[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const animateBodyHeight = useAnimatedHeight<HTMLDivElement>();

  const handleError = (error: unknown) => reportConversationError(t, error);

  useEffect(() => {
    (async () => {
      try {
        const conversation = await fetchConversation();
        setConversation(conversation);
      } catch (error) {
        handleError(error);
      }
    })()
  }, []);

/* No longer need to scroll to last since we show most recent comment first.  
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [conversation]);*/

  const onSubmit = async () => {
    if (!inputText || appState.isReadOnlyAccessRole()) return;

    setIsLoading(true);
    try {
      const conversation = await updateConversation(inputText);
      setConversation(conversation);
      setInputText('');
    } catch (error) {
      handleError(error);
    }
    setIsLoading(false);
  }

  const openModal = () => {
    setInputText('');
    setIsModalShown(prevState => !prevState);
    /*setTimeout(() => {
      if (ref.current) {
        ref.current.style.scrollBehavior = 'auto';
        ref.current.scrollTop = ref.current.scrollHeight;
        ref.current.style.scrollBehavior = 'smooth';
      }
    }, 10);*/
  }

  return (
    <div className={styles.container}>
      {render(
        !!conversation[conversation.length - 1]?.content,
        htmlToPlainTest(conversation[conversation.length - 1]?.content),
        openModal,
        conversation.length,
        conversation[conversation.length - 1]?.addedBy.name,
        new Date(conversation[conversation.length - 1]?.addedAt),
      )}

      <Modal
        show={isModalShown}
        setShow={setIsModalShown}
        title={<>{title} <Badge style={{ fontSize: '14px' }}>{conversation.length}</Badge></>}        
        hasCloseButton
        unmountWhenHidden
        isScrollable={true}
      >
        <div className={styles.modalContainer} ref={animateBodyHeight}>
          {!appState.isReadOnlyAccessRole() && (
            <div style={{ borderBottom: '1px solid var(--gray-200)', paddingBottom: '24px' }}>
              <Editor value={inputText} onChange={setInputText} wrapperStyle={{ width: '600px', height: '200px' }} />
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <BtButton onClick={onSubmit} loaderShow={isLoading} disabled={!inputText}>
                  {t('button.add', 'Lägg till')}
                </BtButton>
              </div>
            </div>
          )}
          <div className={`${styles.conversationContainer} ${appState.isReadOnlyAccessRole() ? styles.readOnly : ''}`} ref={ref}>
            {conversation?.toReversed().map((message) => (
              <ConversationMessage
                key={message._id}
                content={message.content}
                authorName={message.addedBy.name}
                authorAvatarUrl={message.addedBy.avatarUrl}
                date={message.addedAt}
                updatedAt={message.updatedAt}
                canEdit={!appState.isReadOnlyAccessRole() && message.addedBy._id === appState.getUserId()}
                onSave={editMessage ? async (newContent) => {
                  setConversation(await editMessage(message._id, newContent));
                } : undefined}
                onDelete={deleteMessage ? async () => {
                  setConversation(await deleteMessage(message._id));
                } : undefined}
              />
            ))}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ConversationModal;
